import React from "react";
import { ShieldCheck, FilePdf, Database, ArrowsClockwise, LockKey, Handshake } from "@phosphor-icons/react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";

const PDF_URL = "/ttnz-privacy-policy-11.pdf";

const COMMITMENTS = [
  { icon: Database, title: "What we collect", body: "We collect the personal information you provide when you register — such as your name, date of birth, gender, contact details and club — so we can manage your membership." },
  { icon: ArrowsClockwise, title: "How we use it", body: "Your information is used to run competitions and events, maintain accurate membership records, communicate with you, and report anonymised participation data to Table Tennis New Zealand." },
  { icon: LockKey, title: "How we store & protect it", body: "Your information is held securely in the Hello Club membership management system and is only accessed by authorised Te Manawa and TTNZ people who need it to do their role." },
  { icon: Handshake, title: "Who we share it with", body: "We disclose your information to Table Tennis New Zealand as our national body. We do not sell your information or share it for marketing." },
  { icon: ShieldCheck, title: "Your rights", body: "You can ask to see the personal information we hold about you, request corrections, and raise any privacy concern with the Te Manawa Board at any time." },
];

const AGREEMENT_TERMS = [
  "Comply with TTNZ Privacy Policy 11 (dated May 2026).",
  "Ensure all members are informed of TTNZ Privacy Policy 11 regarding the collection, use, storage and disclosure of their personal information, and their rights under this policy.",
  "Agree to the terms and conditions of the Hello Club membership management system (MMS), and ensure members do the same.",
  "Agree, and ensure members agree, to TTNZ collecting personal information through the MMS.",
  "Align ethnicity categories and reporting fields with the standardised reporting framework established by TTNZ for consistent reporting across all Regional Associations.",
  "Ensure all members agree to the terms of the TTNZ Privacy Policy 11 and the Regional Association Privacy Policy.",
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo title="Privacy Policy — Te Manawa Table Tennis Association" description="How Te Manawa Table Tennis collects, uses, stores and shares your personal information, in line with TTNZ Privacy Policy 11 (May 2026)." />
      <PageHero overline="Privacy" title="Privacy Policy" subtitle="How Te Manawa looks after your personal information — aligned with TTNZ Privacy Policy 11 (May 2026)." />

      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-start gap-4 bg-white border border-navy/10 p-6 md:p-8">
              <ShieldCheck size={28} weight="duotone" className="text-gold shrink-0 mt-1" />
              <p className="text-navy/75 leading-relaxed text-lg">
                Te Manawa Table Tennis Association has agreed to Table Tennis New Zealand's
                Privacy Policy 11. When you register as a member — or when a club affiliates with
                Te Manawa — you agree to abide by this policy and to how your personal information
                is collected, used, stored and disclosed.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="font-heading text-3xl md:text-4xl text-navy font-medium tracking-tight mb-6 flex items-center gap-3">
              <span className="text-gold text-lg font-semibold">01</span> Your privacy in plain language
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {COMMITMENTS.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="bg-white border border-navy/10 p-6" data-testid="privacy-commitment">
                    <Icon size={26} weight="duotone" className="text-gold mb-3" />
                    <h3 className="font-heading text-lg text-navy mb-1">{c.title}</h3>
                    <p className="text-navy/70 leading-relaxed text-sm">{c.body}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="font-heading text-3xl md:text-4xl text-navy font-medium tracking-tight mb-6 flex items-center gap-3">
              <span className="text-gold text-lg font-semibold">02</span> What members &amp; clubs agree to
            </h2>
            <div className="bg-white border border-navy/10 p-6 md:p-8">
              <p className="text-navy/70 mb-5">By registering with Te Manawa, members and affiliated clubs agree to:</p>
              <ol className="space-y-4">
                {AGREEMENT_TERMS.map((t, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-heading text-gold font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-navy/80 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal className="mt-10">
            <div className="bg-navy-dark text-white/85 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <p className="text-sm leading-relaxed">
                This page summarises the agreement Te Manawa has signed with Table Tennis New Zealand.
                To read the full signed agreement, or to raise a privacy request, download the document or
                contact the <a href="/contact" className="text-gold hover:underline">Te Manawa Board</a>.
              </p>
              <a href={PDF_URL} target="_blank" rel="noreferrer" data-testid="privacy-pdf-link"
                className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-6 py-3 rounded-sm hover:bg-white transition-colors shrink-0 whitespace-nowrap">
                <FilePdf size={18} weight="fill" /> Download the agreement (PDF)
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
